import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext, Campaign, Donation, Payout } from '../contexts/AppContext';
import { formatCurrency, formatDate, formatRelativeTime, generateReference } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { toast } from 'sonner';
import { Heart, Plus, Search, Filter, Users, Calendar, Target, ChevronLeft, Share2, ShieldCheck, MapPin, Clock, ArrowRight, CheckCircle2, AlertCircle, Info, Building2, Wallet, FileText, UploadCloud, ChevronRight, LayoutDashboard } from 'lucide-react';
// --- Types ---
type CrowdfundingView = 'discover' | 'campaign-detail' | 'create-wizard' | 'my-dashboard';
// --- Main Component ---
export function Crowdfunding() {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const {
    user,
    campaigns,
    donations,
    payouts
  } = state;
  const [currentView, setCurrentView] = useState<CrowdfundingView>('discover');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const handleNavigate = (view: CrowdfundingView, campaignId?: string) => {
    setCurrentView(view);
    if (campaignId !== undefined) {
      setSelectedCampaignId(campaignId);
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const selectedCampaign = useMemo(() => {
    return campaigns.find(c => c.id === selectedCampaignId) || null;
  }, [campaigns, selectedCampaignId]);
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-415">
      <AnimatePresence mode="wait" data-id="element-416">
        {currentView === 'discover' && <motion.div key="discover" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} data-id="element-417">
            <DiscoverView onNavigate={handleNavigate} campaigns={campaigns} data-id="element-418" />
          </motion.div>}

        {currentView === 'campaign-detail' && selectedCampaign && <motion.div key="detail" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} data-id="element-419">
            <CampaignDetailView campaign={selectedCampaign} onBack={() => handleNavigate('discover')} user={user} dispatch={dispatch} processTransaction={processTransaction} data-id="element-420" />
          </motion.div>}

        {currentView === 'create-wizard' && <motion.div key="wizard" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} data-id="element-421">
            <CreateWizardView onCancel={() => handleNavigate('discover')} onComplete={campaignId => handleNavigate('campaign-detail', campaignId)} user={user} dispatch={dispatch} data-id="element-422" />
          </motion.div>}

        {currentView === 'my-dashboard' && <motion.div key="dashboard" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} data-id="element-423">
            <DashboardView onNavigate={handleNavigate} user={user} campaigns={campaigns} donations={donations} payouts={payouts} data-id="element-424" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Sub-components ---
function DiscoverView({
  onNavigate,
  campaigns
}: {
  onNavigate: (v: CrowdfundingView, id?: string) => void;
  campaigns: Campaign[];
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Medical Emergency', 'Education', 'Community Projects', 'Business Capital', 'Memorials & Funerals'];
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase()) || c.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || c.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [campaigns, searchQuery, activeCategory]);
  // Stats for hero
  const totalRaised = campaigns.reduce((sum, c) => sum + c.raisedAmount, 0);
  const totalDonors = campaigns.reduce((sum, c) => sum + (c.donorCount || 0), 0);
  return <div className="space-y-10" data-id="element-425">
      {/* Hero Section */}
      <div className="bg-brand-primary-dark rounded-3xl p-8 md:p-12 text-white relative overflow-hidden" data-id="element-426">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary rounded-full mix-blend-multiply filter blur-3xl opacity-50 translate-x-1/2 -translate-y-1/2" data-id="element-427"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2" data-id="element-428"></div>

        <div className="relative z-10 max-w-3xl" data-id="element-429">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-430">
            Raise Funds for What{' '}
            <span className="text-brand-primary-light" data-id="element-431">Matters Most</span>.
          </h1>
          <p className="text-lg text-white/80 mb-8 max-w-2xl" data-id="element-432">
            Join thousands of Nigerians funding medical emergencies, education,
            community projects, and dreams.
          </p>

          <div className="flex flex-wrap gap-4 mb-10" data-id="element-433">
            <button onClick={() => onNavigate('create-wizard')} className="px-6 py-3 bg-brand-primary hover:bg-brand-primary-light text-brand-text-dark font-semibold rounded-xl transition-colors flex items-center gap-2" data-id="element-434">
              <Plus size={20} data-id="element-435" /> Start a Campaign
            </button>
            <button onClick={() => onNavigate('my-dashboard')} className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl transition-colors flex items-center gap-2" data-id="element-436">
              <LayoutDashboard size={20} data-id="element-437" /> My Dashboard
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 pt-6 border-t border-white/20" data-id="element-438">
            <div className="text-center sm:text-left" data-id="element-439">
              <p className="text-2xl sm:text-3xl font-bold text-white break-words" data-id="element-440">
                {formatCurrency(totalRaised)}
              </p>
              <p className="text-sm text-white/70" data-id="element-441">Total Raised</p>
            </div>
            <div className="text-center sm:text-left" data-id="element-442">
              <p className="text-2xl sm:text-3xl font-bold text-white" data-id="element-443">
                {totalDonors.toLocaleString()}+
              </p>
              <p className="text-sm text-white/70" data-id="element-444">Generous Donors</p>
            </div>
            <div className="text-center sm:text-left" data-id="element-445">
              <p className="text-2xl sm:text-3xl font-bold text-white" data-id="element-446">
                {campaigns.length}
              </p>
              <p className="text-sm text-white/70" data-id="element-447">Active Campaigns</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between sticky top-0 bg-gray-50/80 backdrop-blur-md z-20 py-4" data-id="element-448">
        <div className="flex overflow-x-auto pb-2 md:pb-0 w-full md:w-auto hide-scrollbar gap-2" data-id="element-449">
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === cat ? 'bg-brand-primary text-white shadow-sm' : 'bg-white text-brand-text-light border border-gray-200 hover:border-brand-primary/50'}`} data-id="element-450">
              {cat}
            </button>)}
        </div>
        <div className="relative w-full md:w-72 shrink-0" data-id="element-451">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-452" />
          <input type="text" placeholder="Search campaigns..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-453" />
        </div>
      </div>

      {/* Campaign Grid */}
      {filteredCampaigns.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-id="element-454">
          <Users size={48} className="mx-auto mb-4 text-gray-300" data-id="element-455" />
          <h3 className="text-xl font-semibold text-brand-text-dark mb-2" data-id="element-456">
            No campaigns found
          </h3>
          <p className="text-brand-text-light" data-id="element-457">
            Try adjusting your filters or search query.
          </p>
        </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-458">
          {filteredCampaigns.map(campaign => <CampaignCard key={campaign.id} campaign={campaign} onClick={() => onNavigate('campaign-detail', campaign.id)} data-id="element-459" />)}
        </div>}
    </div>;
}
function CampaignCard({
  campaign,
  onClick
}: {
  campaign: Campaign;
  onClick: () => void;
}) {
  const progress = Math.min(Math.round(campaign.raisedAmount / campaign.targetAmount * 100), 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(campaign.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  return <div onClick={onClick} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group flex flex-col h-full" data-id="element-460">
      <div className="relative h-48 overflow-hidden" data-id="element-461">
        <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-462" />
        {campaign.isUrgent && <div className="absolute top-3 left-3 bg-brand-accent text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm" data-id="element-463">
            <AlertCircle size={12} data-id="element-464" /> URGENT
          </div>}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-brand-primary-dark text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm" data-id="element-465">
          {campaign.category}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1" data-id="element-466">
        <h3 className="font-bold text-brand-text-dark text-lg leading-tight mb-2 line-clamp-2 group-hover:text-brand-primary transition-colors" data-id="element-467">
          {campaign.title}
        </h3>

        <div className="flex items-center gap-2 mb-4" data-id="element-468">
          <img src={campaign.organizerAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.organizerName)}&background=f3f4f6&color=6b7280`} alt={campaign.organizerName} className="w-6 h-6 rounded-full" data-id="element-469" />
          <span className="text-xs text-brand-text-light font-medium truncate" data-id="element-470">
            by {campaign.organizerName}
          </span>
          {campaign.verificationStatus === 'verified' && <ShieldCheck size={14} className="text-brand-primary" title="Verified Organizer" data-id="element-471" />}
        </div>

        <div className="mt-auto space-y-3" data-id="element-472">
          <div data-id="element-473">
            <div className="flex justify-between text-sm mb-1.5" data-id="element-474">
              <span className="font-bold text-brand-text-dark" data-id="element-475">
                {formatCurrency(campaign.raisedAmount)}
              </span>
              <span className="text-brand-text-light text-xs" data-id="element-476">
                raised of {formatCurrency(campaign.targetAmount)}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-477">
              <div className="bg-brand-primary h-full rounded-full transition-all duration-1000 ease-out" style={{
              width: `${progress}%`
            }} data-id="element-478" />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-brand-text-light font-medium" data-id="element-479">
            <span className="flex items-center gap-1" data-id="element-480">
              <Users size={14} data-id="element-481" /> {campaign.donorCount || 0} donors
            </span>
            <span className="flex items-center gap-1" data-id="element-482">
              <Clock size={14} data-id="element-483" />{' '}
              {daysLeft > 0 ? `${daysLeft} days left` : 'Ended'}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
function CampaignDetailView({
  campaign,
  onBack,
  user,
  dispatch,
  processTransaction
}: {
  campaign: Campaign;
  onBack: () => void;
  user: any;
  dispatch: any;
  processTransaction: any;
}) {
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donationMessage, setDonationMessage] = useState('');
  const progress = Math.min(Math.round(campaign.raisedAmount / campaign.targetAmount * 100), 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(campaign.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const handleDonate = () => {
    const amount = Number(donationAmount);
    if (!amount || amount < 500) {
      toast.error('Minimum donation is ₦500');
      return;
    }
    if (!user || amount > user.walletBalance) {
      toast.error('Insufficient wallet balance. Please add funds.');
      return;
    }
    // Process transaction
    const ref = processTransaction(-amount, 'Crowdfunding', `Donation to: ${campaign.title}`);
    // Create donation record
    const donation: Donation = {
      id: `don_${Date.now()}`,
      campaignId: campaign.id,
      donorId: user.id,
      donorName: isAnonymous ? 'Anonymous' : user.name,
      amount,
      tipAmount: 0,
      message: donationMessage,
      isAnonymous,
      isRecurring: false,
      paymentMethod: 'wallet',
      transactionReference: ref,
      status: 'completed',
      createdAt: new Date().toISOString()
    };
    dispatch({
      type: 'ADD_DONATION',
      payload: donation
    });
    dispatch({
      type: 'UPDATE_CAMPAIGN_RAISED',
      payload: {
        id: campaign.id,
        amount
      }
    });
    toast.success('Donation successful!', {
      description: `Thank you for supporting "${campaign.title}"`
    });
    setIsDonateModalOpen(false);
    setDonationAmount('');
    setDonationMessage('');
  };
  return <div className="max-w-6xl mx-auto" data-id="element-484">
      <button onClick={onBack} className="flex items-center gap-2 text-brand-text-light hover:text-brand-text-dark mb-6 transition-colors font-medium" data-id="element-485">
        <ChevronLeft size={20} data-id="element-486" /> Back to Discover
      </button>

      <div className="grid lg:grid-cols-3 gap-8" data-id="element-487">
        {/* Left Column - Content */}
        <div className="lg:col-span-2 space-y-8" data-id="element-488">
          <div className="space-y-4" data-id="element-489">
            <h1 className="text-3xl md:text-4xl font-bold text-brand-text-dark leading-tight" data-id="element-490">
              {campaign.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-brand-text-light" data-id="element-491">
              <span className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full" data-id="element-492">
                <Target size={16} data-id="element-493" /> {campaign.category}
              </span>
              {campaign.location && <span className="flex items-center gap-1" data-id="element-494">
                  <MapPin size={16} data-id="element-495" /> {campaign.location.city},{' '}
                  {campaign.location.state}
                </span>}
              <span className="flex items-center gap-1" data-id="element-496">
                <Calendar size={16} data-id="element-497" /> Created {formatDate(campaign.createdAt)}
              </span>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-video relative" data-id="element-498">
            <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-full object-cover" data-id="element-499" />
            {campaign.isUrgent && <div className="absolute top-4 left-4 bg-brand-accent text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md" data-id="element-500">
                <AlertCircle size={16} data-id="element-501" /> URGENT
              </div>}
          </div>

          {/* Organizer Info */}
          <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-502">
            <img src={campaign.organizerAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.organizerName)}&background=f3f4f6&color=6b7280`} alt={campaign.organizerName} className="w-14 h-14 rounded-full" data-id="element-503" />
            <div className="flex-1" data-id="element-504">
              <p className="text-sm text-brand-text-light mb-0.5" data-id="element-505">
                Organized by
              </p>
              <div className="flex items-center gap-2" data-id="element-506">
                <h3 className="font-bold text-brand-text-dark text-lg" data-id="element-507">
                  {campaign.organizerName}
                </h3>
                {campaign.verificationStatus === 'verified' && <span className="flex items-center gap-1 text-xs font-medium text-brand-primary bg-brand-primary/10 px-2 py-0.5 rounded-full" data-id="element-508">
                    <ShieldCheck size={14} data-id="element-509" /> Verified
                  </span>}
              </div>
            </div>
            <button className="px-4 py-2 text-sm font-medium text-brand-primary border border-brand-primary/30 rounded-xl hover:bg-brand-primary/5 transition-colors" data-id="element-510">
              Contact
            </button>
          </div>

          {/* Story */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8" data-id="element-511">
            <h2 className="text-xl font-bold text-brand-text-dark mb-6" data-id="element-512">
              Story
            </h2>
            {campaign.story ? <div className="prose prose-brand max-w-none text-brand-text-light" dangerouslySetInnerHTML={{
            __html: campaign.story
          }} data-id="element-513" /> : <p className="text-brand-text-light whitespace-pre-wrap" data-id="element-514">
                {campaign.description}
              </p>}

            {campaign.useOfFunds && campaign.useOfFunds.length > 0 && <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-515">
                <h3 className="font-bold text-brand-text-dark mb-4" data-id="element-516">
                  Use of Funds
                </h3>
                <div className="space-y-3" data-id="element-517">
                  {campaign.useOfFunds.map((item, idx) => <div key={idx} className="flex items-center gap-4" data-id="element-518">
                      <div className="w-16 text-sm font-medium text-brand-text-dark" data-id="element-519">
                        {item.percentage}%
                      </div>
                      <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden" data-id="element-520">
                        <div className="bg-brand-primary-medium h-full rounded-full" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-521" />
                      </div>
                      <div className="w-32 text-sm text-brand-text-light" data-id="element-522">
                        {item.item}
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Right Column - Sticky Donation Card */}
        <div className="lg:col-span-1" data-id="element-523">
          <div className="sticky top-24 space-y-6" data-id="element-524">
            <div className="bg-white rounded-3xl border-2 border-brand-primary/20 shadow-xl p-6 md:p-8" data-id="element-525">
              <div className="mb-6" data-id="element-526">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-527">
                  <span className="text-3xl font-bold text-brand-text-dark" data-id="element-528">
                    {formatCurrency(campaign.raisedAmount)}
                  </span>
                  <span className="text-brand-text-light" data-id="element-529">raised</span>
                </div>
                <p className="text-sm text-brand-text-light mb-4" data-id="element-530">
                  of {formatCurrency(campaign.targetAmount)} goal
                </p>

                <div className="w-full bg-gray-100 rounded-full h-2 mb-2" data-id="element-531">
                  <div className="bg-brand-primary h-full rounded-full transition-all duration-1000 ease-out" style={{
                  width: `${progress}%`
                }} data-id="element-532" />
                </div>
                <div className="flex justify-between text-sm font-medium text-brand-text-dark" data-id="element-533">
                  <span data-id="element-534">{progress}% funded</span>
                  <span data-id="element-535">{campaign.donorCount || 0} donations</span>
                </div>
              </div>

              <button onClick={() => setIsDonateModalOpen(true)} className="w-full py-4 bg-brand-primary hover:bg-brand-primary-medium text-white text-lg font-bold rounded-xl transition-colors shadow-md shadow-brand-primary/30 flex items-center justify-center gap-2 mb-4" data-id="element-536">
                <Heart size={20} data-id="element-537" /> Donate Now
              </button>

              <button className="w-full py-3 bg-white border-2 border-gray-200 hover:border-gray-300 text-brand-text-dark font-semibold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-538">
                <Share2 size={18} data-id="element-539" /> Share Campaign
              </button>

              <div className="mt-6 pt-6 border-t border-gray-100 flex items-start gap-3 text-sm text-brand-text-light" data-id="element-540">
                <ShieldCheck size={20} className="text-brand-primary shrink-0 mt-0.5" data-id="element-541" />
                <p data-id="element-542">
                  <strong className="text-brand-text-dark block mb-1" data-id="element-543">
                    Billvast Guarantee
                  </strong>
                  Your donation is protected. If something isn't right, we will
                  refund your donation.
                </p>
              </div>
            </div>

            {/* Recent Donations Mock */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-544">
              <h3 className="font-bold text-brand-text-dark mb-4 flex items-center gap-2" data-id="element-545">
                <Heart size={18} className="text-brand-primary" data-id="element-546" /> Recent
                Donations
              </h3>
              <div className="space-y-4" data-id="element-547">
                {[1, 2, 3].map(i => <div key={i} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-548">
                    <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0" data-id="element-549">
                      <Heart size={16} className="fill-current" data-id="element-550" />
                    </div>
                    <div data-id="element-551">
                      <p className="font-medium text-brand-text-dark text-sm" data-id="element-552">
                        {i === 2 ? 'Anonymous' : `Donor ${i}`}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-brand-text-light mt-0.5" data-id="element-553">
                        <span className="font-bold text-brand-text-dark" data-id="element-554">
                          {formatCurrency(i * 5000)}
                        </span>
                        <span data-id="element-555">•</span>
                        <span data-id="element-556">{i} hr ago</span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Donate Modal */}
      <Modal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} title="Support this cause" maxWidth="max-w-md" data-id="element-557">
        <div className="space-y-6" data-id="element-558">
          <div data-id="element-559">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-560">
              Enter your donation
            </label>
            <div className="relative" data-id="element-561">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-lg" data-id="element-562">
                ₦
              </span>
              <input type="number" value={donationAmount} onChange={e => setDonationAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-4 text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-brand-primary focus:ring-0 outline-none transition-colors" data-id="element-563" />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3" data-id="element-564">
              {[5000, 10000, 25000, 50000].map(amt => <button key={amt} onClick={() => setDonationAmount(amt.toString())} className="py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-565">
                  {amt / 1000}k
                </button>)}
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-100" data-id="element-566">
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-567">
              <div className="relative flex items-center justify-center mt-0.5" data-id="element-568">
                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} className="peer sr-only" data-id="element-569" />
                <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:bg-brand-primary peer-checked:border-brand-primary transition-colors" data-id="element-570"></div>
                <CheckCircle2 size={14} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity" data-id="element-571" />
              </div>
              <div data-id="element-572">
                <p className="text-sm font-medium text-brand-text-dark" data-id="element-573">
                  Don't display my name publicly
                </p>
              </div>
            </label>
          </div>

          <div data-id="element-574">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-575">
              Leave a message of support (optional)
            </label>
            <textarea value={donationMessage} onChange={e => setDonationMessage(e.target.value)} placeholder="Words of encouragement..." rows={3} className="w-full p-3 border border-gray-200 rounded-xl focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none resize-none text-sm" data-id="element-576" />
          </div>

          <div className="bg-gray-50 p-4 rounded-xl space-y-2" data-id="element-577">
            <div className="flex justify-between text-sm" data-id="element-578">
              <span className="text-brand-text-light" data-id="element-579">Donation</span>
              <span className="font-medium" data-id="element-580">
                {formatCurrency(Number(donationAmount) || 0)}
              </span>
            </div>
            <div className="flex justify-between font-bold text-brand-text-dark pt-2 border-t border-gray-200" data-id="element-581">
              <span data-id="element-582">Total</span>
              <span data-id="element-583">{formatCurrency(Number(donationAmount) || 0)}</span>
            </div>
            <div className="flex justify-between text-xs text-brand-text-light pt-1" data-id="element-584">
              <span data-id="element-585">Wallet Balance</span>
              <span data-id="element-586">{formatCurrency(user?.walletBalance || 0)}</span>
            </div>
          </div>

          <button onClick={handleDonate} className="w-full py-4 bg-brand-primary hover:bg-brand-primary-medium text-white font-bold rounded-xl transition-colors shadow-md shadow-brand-primary/20" data-id="element-587">
            Donate {formatCurrency(Number(donationAmount) || 0)}
          </button>
        </div>
      </Modal>
    </div>;
}
// --- Create Wizard View ---
function CreateWizardView({
  onCancel,
  onComplete,
  user,
  dispatch
}: any) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Medical Emergency',
    targetAmount: '',
    endDate: '',
    locationState: 'Lagos',
    locationCity: '',
    shortDescription: '',
    story: ''
  });
  const categories = ['Medical Emergency', 'Education', 'Community Projects', 'Business Capital', 'Memorials & Funerals'];
  const handleNext = () => setStep(s => Math.min(s + 1, 3));
  const handlePrev = () => setStep(s => Math.max(s - 1, 1));
  const handleSubmit = () => {
    if (!formData.title || !formData.targetAmount || !formData.story) {
      toast.error('Please fill in all required fields');
      return;
    }
    const campaign: Campaign = {
      id: `camp_${Date.now()}`,
      title: formData.title,
      shortDescription: formData.shortDescription || formData.title,
      description: formData.shortDescription || formData.title,
      story: `<p>${formData.story.replace(/\n/g, '</p><p>')}</p>`,
      targetAmount: Number(formData.targetAmount),
      raisedAmount: 0,
      category: formData.category,
      endDate: new Date(formData.endDate || Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Active',
      imageUrl: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb0?auto=format&fit=crop&q=80&w=800',
      createdAt: new Date().toISOString(),
      userId: user?.id || 'usr_1',
      organizerName: user?.name || 'User',
      organizerAvatar: user?.avatar,
      donorCount: 0,
      isUrgent: formData.category === 'Medical Emergency',
      location: {
        state: formData.locationState,
        city: formData.locationCity
      },
      verificationStatus: 'pending'
    };
    dispatch({
      type: 'CREATE_CAMPAIGN',
      payload: campaign
    });
    toast.success('Campaign created successfully!');
    onComplete(campaign.id);
  };
  return <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-588">
      <div className="flex border-b border-gray-100" data-id="element-589">
        {[1, 2, 3].map(i => <div key={i} className={`flex-1 py-4 text-center text-sm font-medium border-b-2 transition-colors ${step === i ? 'border-brand-primary text-brand-primary' : step > i ? 'border-brand-primary/30 text-brand-text-dark' : 'border-transparent text-brand-text-light'}`} data-id="element-590">
            Step {i}
          </div>)}
      </div>

      <div className="p-8 md:p-12" data-id="element-591">
        {step === 1 && <div className="space-y-6 animate-fade-in" data-id="element-592">
            <h2 className="text-2xl font-bold text-brand-text-dark mb-6" data-id="element-593">
              Let's start with the basics
            </h2>

            <div data-id="element-594">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-595">
                Campaign Title
              </label>
              <input type="text" value={formData.title} onChange={e => setFormData({
            ...formData,
            title: e.target.value
          })} placeholder="e.g., Help Amina beat cancer" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all" data-id="element-596" />
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-597">
              <div data-id="element-598">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-599">
                  Category
                </label>
                <select value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all" data-id="element-600">
                  {categories.map(c => <option key={c} value={c} data-id="element-601">
                      {c}
                    </option>)}
                </select>
              </div>
              <div data-id="element-602">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-603">
                  Goal Amount (₦)
                </label>
                <input type="number" value={formData.targetAmount} onChange={e => setFormData({
              ...formData,
              targetAmount: e.target.value
            })} placeholder="500000" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all" data-id="element-604" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-605">
              <div data-id="element-606">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-607">
                  State
                </label>
                <input type="text" value={formData.locationState} onChange={e => setFormData({
              ...formData,
              locationState: e.target.value
            })} placeholder="e.g., Lagos" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all" data-id="element-608" />
              </div>
              <div data-id="element-609">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-610">
                  City
                </label>
                <input type="text" value={formData.locationCity} onChange={e => setFormData({
              ...formData,
              locationCity: e.target.value
            })} placeholder="e.g., Ikeja" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all" data-id="element-611" />
              </div>
            </div>
          </div>}

        {step === 2 && <div className="space-y-6 animate-fade-in" data-id="element-612">
            <h2 className="text-2xl font-bold text-brand-text-dark mb-6" data-id="element-613">
              Tell your story
            </h2>

            <div data-id="element-614">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-615">
                Short Summary (for campaign card)
              </label>
              <textarea value={formData.shortDescription} onChange={e => setFormData({
            ...formData,
            shortDescription: e.target.value
          })} placeholder="Briefly describe why you are raising funds..." rows={2} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all resize-none" data-id="element-616" />
            </div>

            <div data-id="element-617">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-618">
                Full Story
              </label>
              <textarea value={formData.story} onChange={e => setFormData({
            ...formData,
            story: e.target.value
          })} placeholder="Tell potential donors the full story. Be honest, detailed, and explain exactly how the funds will be used..." rows={8} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all resize-none" data-id="element-619" />
            </div>

            <div data-id="element-620">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-621">
                Cover Image
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-622">
                <UploadCloud size={32} className="mx-auto text-gray-400 mb-3" data-id="element-623" />
                <p className="text-sm font-medium text-brand-text-dark" data-id="element-624">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-brand-text-light mt-1" data-id="element-625">
                  SVG, PNG, JPG or GIF (max. 5MB)
                </p>
              </div>
            </div>
          </div>}

        {step === 3 && <div className="space-y-6 animate-fade-in" data-id="element-626">
            <h2 className="text-2xl font-bold text-brand-text-dark mb-6" data-id="element-627">
              Review & Publish
            </h2>

            <div className="bg-gray-50 rounded-2xl p-6 space-y-4" data-id="element-628">
              <div data-id="element-629">
                <p className="text-sm text-brand-text-light" data-id="element-630">Campaign Title</p>
                <p className="font-semibold text-brand-text-dark" data-id="element-631">
                  {formData.title || 'Not set'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-632">
                <div data-id="element-633">
                  <p className="text-sm text-brand-text-light" data-id="element-634">Goal</p>
                  <p className="font-semibold text-brand-text-dark" data-id="element-635">
                    {formData.targetAmount ? formatCurrency(Number(formData.targetAmount)) : 'Not set'}
                  </p>
                </div>
                <div data-id="element-636">
                  <p className="text-sm text-brand-text-light" data-id="element-637">Category</p>
                  <p className="font-semibold text-brand-text-dark" data-id="element-638">
                    {formData.category}
                  </p>
                </div>
              </div>
              <div data-id="element-639">
                <p className="text-sm text-brand-text-light" data-id="element-640">Location</p>
                <p className="font-semibold text-brand-text-dark" data-id="element-641">
                  {formData.locationCity}, {formData.locationState}
                </p>
              </div>
            </div>

            <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm flex items-start gap-3" data-id="element-642">
              <Info size={20} className="shrink-0 mt-0.5" data-id="element-643" />
              <p data-id="element-644">
                Your campaign will be marked as "Pending Verification". You can
                start receiving donations immediately, but withdrawals require
                ID verification.
              </p>
            </div>
          </div>}

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100" data-id="element-645">
          <button onClick={step === 1 ? onCancel : handlePrev} className="px-6 py-3 text-brand-text-light font-medium hover:text-brand-text-dark transition-colors" data-id="element-646">
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <button onClick={step === 3 ? handleSubmit : handleNext} className="px-8 py-3 bg-brand-primary hover:bg-brand-primary-medium text-white font-bold rounded-xl transition-colors shadow-md shadow-brand-primary/20 flex items-center gap-2" data-id="element-647">
            {step === 3 ? 'Publish Campaign' : 'Next Step'}
            {step !== 3 && <ChevronRight size={18} data-id="element-648" />}
          </button>
        </div>
      </div>
    </div>;
}
// --- Dashboard View ---
function DashboardView({
  onNavigate,
  user,
  campaigns,
  donations,
  payouts
}: any) {
  const [activeTab, setActiveTab] = useState<'campaigns' | 'donations' | 'payouts'>('campaigns');
  const myCampaigns = campaigns.filter((c: Campaign) => c.userId === user?.id);
  const myDonations = donations.filter((d: Donation) => d.donorId === user?.id);
  const myPayouts = payouts.filter((p: Payout) => p.organizerId === user?.id);
  return <div className="max-w-6xl mx-auto space-y-8" data-id="element-649">
      <div className="flex items-center justify-between" data-id="element-650">
        <div data-id="element-651">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-652">
            My Dashboard
          </h1>
          <p className="text-brand-text-light mt-1" data-id="element-653">
            Manage your campaigns and track your impact.
          </p>
        </div>
        <button onClick={() => onNavigate('discover')} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-654">
          Back to Discover
        </button>
      </div>

      <div className="flex gap-2 border-b border-gray-200" data-id="element-655">
        {[{
        id: 'campaigns',
        label: 'My Campaigns',
        count: myCampaigns.length
      }, {
        id: 'donations',
        label: 'My Donations',
        count: myDonations.length
      }, {
        id: 'payouts',
        label: 'Payout History',
        count: myPayouts.length
      }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-brand-primary text-brand-primary' : 'border-transparent text-brand-text-light hover:text-brand-text-dark'}`} data-id="element-656">
            {tab.label}{' '}
            <span className="ml-1 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs" data-id="element-657">
              {tab.count}
            </span>
          </button>)}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-658">
        {activeTab === 'campaigns' && <div className="space-y-6" data-id="element-659">
            <div className="flex justify-end" data-id="element-660">
              <button onClick={() => onNavigate('create-wizard')} className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary-medium transition-colors flex items-center gap-2" data-id="element-661">
                <Plus size={16} data-id="element-662" /> New Campaign
              </button>
            </div>

            {myCampaigns.length === 0 ? <div className="text-center py-12" data-id="element-663">
                <Target size={40} className="mx-auto text-gray-300 mb-3" data-id="element-664" />
                <p className="text-brand-text-dark font-medium" data-id="element-665">
                  No campaigns yet
                </p>
                <p className="text-sm text-brand-text-light mt-1" data-id="element-666">
                  Start your first campaign to raise funds.
                </p>
              </div> : <div className="grid gap-4" data-id="element-667">
                {myCampaigns.map((c: Campaign) => <div key={c.id} className="flex flex-col sm:flex-row gap-4 p-4 border border-gray-100 rounded-xl hover:border-brand-primary/30 transition-colors" data-id="element-668">
                    <img src={c.imageUrl} alt={c.title} className="w-full sm:w-32 h-24 object-cover rounded-lg" data-id="element-669" />
                    <div className="flex-1" data-id="element-670">
                      <div className="flex justify-between items-start mb-1" data-id="element-671">
                        <h4 className="font-bold text-brand-text-dark line-clamp-1" data-id="element-672">
                          {c.title}
                        </h4>
                        <StatusBadge status={c.status} data-id="element-673" />
                      </div>
                      <p className="text-xs text-brand-text-light mb-3" data-id="element-674">
                        Created {formatDate(c.createdAt)}
                      </p>
                      <div className="flex items-center gap-4 text-sm" data-id="element-675">
                        <div data-id="element-676">
                          <span className="font-bold text-brand-text-dark" data-id="element-677">
                            {formatCurrency(c.raisedAmount)}
                          </span>
                          <span className="text-brand-text-light ml-1" data-id="element-678">
                            raised
                          </span>
                        </div>
                        <div className="w-px h-4 bg-gray-200" data-id="element-679"></div>
                        <div data-id="element-680">
                          <span className="font-bold text-brand-text-dark" data-id="element-681">
                            {c.donorCount || 0}
                          </span>
                          <span className="text-brand-text-light ml-1" data-id="element-682">
                            donors
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2 justify-end" data-id="element-683">
                      <button onClick={() => onNavigate('campaign-detail', c.id)} className="px-4 py-2 text-sm font-medium text-brand-primary bg-brand-primary/10 rounded-lg hover:bg-brand-primary/20 transition-colors" data-id="element-684">
                        View
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-685">
                        Manage
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>}

        {activeTab === 'donations' && <div data-id="element-686">
            {myDonations.length === 0 ? <div className="text-center py-12" data-id="element-687">
                <Heart size={40} className="mx-auto text-gray-300 mb-3" data-id="element-688" />
                <p className="text-brand-text-dark font-medium" data-id="element-689">
                  No donations yet
                </p>
                <p className="text-sm text-brand-text-light mt-1" data-id="element-690">
                  Support a cause to see your impact here.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-691">
                <table className="w-full text-sm text-left" data-id="element-692">
                  <thead className="text-xs text-brand-text-light uppercase bg-gray-50" data-id="element-693">
                    <tr data-id="element-694">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-695">Campaign</th>
                      <th className="px-4 py-3" data-id="element-696">Amount</th>
                      <th className="px-4 py-3" data-id="element-697">Date</th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-698">Status</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-699">
                    {myDonations.map((d: Donation) => {
                const camp = campaigns.find((c: Campaign) => c.id === d.campaignId);
                return <tr key={d.id} className="border-b border-gray-50 last:border-0" data-id="element-700">
                          <td className="px-4 py-4 font-medium text-brand-text-dark" data-id="element-701">
                            {camp?.title || 'Unknown Campaign'}
                          </td>
                          <td className="px-4 py-4 font-bold" data-id="element-702">
                            {formatCurrency(d.amount)}
                          </td>
                          <td className="px-4 py-4 text-brand-text-light" data-id="element-703">
                            {formatDate(d.createdAt)}
                          </td>
                          <td className="px-4 py-4" data-id="element-704">
                            <StatusBadge status={d.status} data-id="element-705" />
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}
          </div>}

        {activeTab === 'payouts' && <div data-id="element-706">
            {myPayouts.length === 0 ? <div className="text-center py-12" data-id="element-707">
                <Wallet size={40} className="mx-auto text-gray-300 mb-3" data-id="element-708" />
                <p className="text-brand-text-dark font-medium" data-id="element-709">
                  No payouts yet
                </p>
                <p className="text-sm text-brand-text-light mt-1" data-id="element-710">
                  Withdraw funds from your campaigns to see them here.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-711">
                <table className="w-full text-sm text-left" data-id="element-712">
                  <thead className="text-xs text-brand-text-light uppercase bg-gray-50" data-id="element-713">
                    <tr data-id="element-714">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-715">Campaign</th>
                      <th className="px-4 py-3" data-id="element-716">Amount</th>
                      <th className="px-4 py-3" data-id="element-717">Bank Details</th>
                      <th className="px-4 py-3" data-id="element-718">Date</th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-719">Status</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-720">
                    {myPayouts.map((p: Payout) => <tr key={p.id} className="border-b border-gray-50 last:border-0" data-id="element-721">
                        <td className="px-4 py-4 font-medium text-brand-text-dark" data-id="element-722">
                          {p.campaignTitle}
                        </td>
                        <td className="px-4 py-4 font-bold" data-id="element-723">
                          {formatCurrency(p.netAmount)}
                        </td>
                        <td className="px-4 py-4 text-brand-text-light text-xs" data-id="element-724">
                          {p.bankName}
                          <br data-id="element-725" />
                          {p.accountNumber}
                        </td>
                        <td className="px-4 py-4 text-brand-text-light" data-id="element-726">
                          {formatDate(p.createdAt)}
                        </td>
                        <td className="px-4 py-4" data-id="element-727">
                          <StatusBadge status={p.status} data-id="element-728" />
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}
      </div>
    </div>;
}